import React, { useState, useEffect, useCallback } from "react";
import { X, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "pwa-install-dismissed-at";
const DISMISS_DAYS = 7;

function isDismissed(): boolean {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const dismissedAt = Number(raw);
  const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
  return daysSince < DISMISS_DAYS;
}

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

function isAndroid(): boolean {
  return /Android/.test(navigator.userAgent);
}

function isInStandaloneMode(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as any).standalone === true
  );
}

function getGlobalPrompt(): BeforeInstallPromptEvent | null {
  return (window as any).__pwaInstallPrompt?.get?.() ?? null;
}

export const PWAInstallPrompt: React.FC = () => {
  const [hasPrompt, setHasPrompt] = useState(false);
  const [visible, setVisible] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [platform] = useState<'ios' | 'android' | 'desktop'>(() => {
    if (isIOS()) return 'ios';
    if (isAndroid()) return 'android';
    return 'desktop';
  });

  useEffect(() => {
    if (isInStandaloneMode() || isDismissed()) return;

    // Check if global prompt already captured
    if (getGlobalPrompt()) {
      setHasPrompt(true);
      setVisible(true);
      return;
    }

    // Listen for future events
    const handler = (e: Event) => {
      e.preventDefault();
      setHasPrompt(true);
      setShowManual(false);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // For mobile: wait for SW ready + small delay, then show manual fallback if no native prompt
    if (platform === 'ios' || platform === 'android') {
      let cancelled = false;
      const showFallback = () => {
        if (!cancelled && !getGlobalPrompt()) {
          setShowManual(true);
          setVisible(true);
        }
      };

      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then(() => {
          setTimeout(showFallback, 2000);
        });
        // Safety: if SW.ready never resolves, still show after 5s
        setTimeout(showFallback, 5000);
      } else {
        setTimeout(showFallback, 1500);
      }

      return () => {
        cancelled = true;
        window.removeEventListener("beforeinstallprompt", handler);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [platform]);

  // Hide on appinstalled
  useEffect(() => {
    const handler = () => setVisible(false);
    window.addEventListener("appinstalled", handler);
    return () => window.removeEventListener("appinstalled", handler);
  }, []);

  const handleInstall = useCallback(async () => {
    const prompt = getGlobalPrompt();
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
      import('@/services/pwaInstalls').then(m => m.trackPwaInstall());
    }
    (window as any).__pwaInstallPrompt?.clear?.();
    setHasPrompt(false);
  }, []);

  const handleDismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[9999] mx-auto max-w-md animate-in slide-in-from-bottom-4 duration-300">
      <div className="rounded-xl border border-border bg-card p-4 shadow-lg backdrop-blur-sm">
        <button
          onClick={handleDismiss}
          className="absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Fechar"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <Download className="h-5 w-5 text-primary" />
          </div>

          <div className="min-w-0 space-y-1">
            <h3 className="text-sm font-semibold text-foreground">
              Instalar ForjaHub
            </h3>

            {hasPrompt ? (
              <p className="text-xs text-muted-foreground leading-relaxed">
                Acesse o ForjaHub direto da sua tela inicial, mais rápido e sem barra do navegador.
              </p>
            ) : showManual && platform === 'ios' ? (
              <ol className="space-y-1 text-xs text-muted-foreground leading-relaxed list-none m-0 p-0">
                <li>
                  <span className="font-bold text-primary">1.</span>{" "}
                  Toque em <strong>Compartilhar</strong> (ícone ↑) na barra do navegador.
                </li>
                <li>
                  <span className="font-bold text-primary">2.</span>{" "}
                  Selecione <strong>"Adicionar à Tela de Início"</strong>.
                </li>
                <li>
                  <span className="font-bold text-primary">3.</span>{" "}
                  Confirme tocando em <strong>"Adicionar"</strong>.
                </li>
              </ol>
            ) : showManual && platform === 'android' ? (
              <ol className="space-y-2 text-xs text-muted-foreground leading-relaxed list-none m-0 p-0">
                <li className="grid grid-cols-[1rem,1fr] gap-2 items-start min-w-0">
                  <span className="font-bold text-primary">1.</span>
                  <span className="min-w-0 break-words">
                    Abra o menu <strong>⋮</strong> do Chrome.
                  </span>
                </li>
                <li className="grid grid-cols-[1rem,1fr] gap-2 items-start min-w-0">
                  <span className="font-bold text-primary">2.</span>
                  <span className="min-w-0 break-words">
                    Toque em <strong>Compartilhar</strong> e procure por <strong>"Adicionar à tela inicial"</strong>, <strong>"Instalar app"</strong> ou <strong>"Criar atalho"</strong>.
                  </span>
                </li>
                <li className="grid grid-cols-[1rem,1fr] gap-2 items-start min-w-0">
                  <span className="font-bold text-primary">3.</span>
                  <span className="min-w-0 break-words">
                    Confirme em <strong>"Adicionar"</strong> ou <strong>"Instalar"</strong>.
                  </span>
                </li>
                <li className="text-muted-foreground/70 mt-1">
                  💡 Se a opção não aparecer, atualize a página 1 vez, aguarde alguns segundos e tente novamente.
                </li>
              </ol>
            ) : (
              <p className="text-xs text-muted-foreground leading-relaxed">
                Acesse o ForjaHub direto da sua tela inicial, mais rápido e sem barra do navegador.
              </p>
            )}
          </div>
        </div>

        {hasPrompt && (
          <div className="mt-3 flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={handleDismiss}>
              Agora não
            </Button>
            <Button size="sm" onClick={handleInstall}>
              Instalar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
